import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
import { FALLBACK_GALLERIES } from '../data';

export default function GalleryModal({ gallerySlug }) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    async function loadGallery() {
      if (!gallerySlug) {
        setGallery(null);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await fetch(`https://api.dev.auxo.dk/public/by-slug/da/${gallerySlug}`);
        if (res.ok) {
          const data = await res.json();
          // Map CMS fields
          if (data && data.images) {
            setGallery({
              title: data.title?.da || data.title || '',
              description: data.description?.da || data.description || '',
              images: data.images
            });
            return;
          }
        }
      } catch (err) {
        console.error('Error fetching gallery from CMS:', err);
      }

      // Fallback
      if (FALLBACK_GALLERIES[gallerySlug]) {
        setGallery(FALLBACK_GALLERIES[gallerySlug]);
      } else {
        setGallery(null);
      }
      setLoading(false);
    }

    loadGallery();
  }, [gallerySlug]);

  if (loading) {
    return null;
  }

  if (!gallery || !gallery.images || gallery.images.length === 0) {
    return null;
  }

  const handlePrev = (e) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev === 0 ? gallery.images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev === gallery.images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="mt-12 border-t border-[#0e1f2f]/10 pt-12">
      <div className="mb-6 flex items-center gap-2 text-xs uppercase tracking-widest text-[#3c8888] font-bold">
        <ImageIcon className="h-4 w-4" />
        <span>Projektgalleri: {gallery.title}</span>
      </div>
      
      {gallery.description && (
        <p className="mb-8 max-w-2xl text-sm text-stone-600">
          {gallery.description}
        </p>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        {gallery.images.map((img, idx) => (
          <button
            key={idx}
            onClick={() => setActiveIndex(idx)}
            className="group relative aspect-4/3 overflow-hidden rounded-lg border border-[#0e1f2f]/10 bg-[#f7f8fa] transition-all duration-300 hover:border-[#3c8888]/30 focus:outline-none focus:ring-2 focus:ring-[#3c8888]/50 shadow-sm"
          >
            <img
              src={img}
              alt={`${gallery.title} - Billede ${idx + 1}`}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-[#0e1f2f]/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex items-center justify-center">
              <span className="rounded-full bg-white/90 p-2.5 text-[#0e1f2f] border border-[#0e1f2f]/10">
                <ChevronRight className="h-5 w-5" />
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      {activeIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#0e1f2f]/95 p-4 backdrop-blur-sm transition-opacity duration-300"
          onClick={() => setActiveIndex(null)}
        >
          <button
            onClick={() => setActiveIndex(null)}
            className="absolute top-4 right-4 z-50 rounded-full bg-[#0e1f2f]/80 p-3 text-stone-300 hover:text-white border border-stone-700 transition-colors focus:outline-none"
            aria-label="Luk visning"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Navigation Buttons */}
          <button
            onClick={handlePrev}
            className="absolute left-4 z-50 rounded-full bg-[#0e1f2f]/80 p-3 text-stone-300 hover:text-white border border-stone-700 transition-colors focus:outline-none select-none"
            aria-label="Forrige billede"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          <button
            onClick={handleNext}
            className="absolute right-4 z-50 rounded-full bg-[#0e1f2f]/80 p-3 text-stone-300 hover:text-white border border-stone-700 transition-colors focus:outline-none select-none"
            aria-label="Næste billede"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          {/* Large Image Container */}
          <div
            className="relative max-h-[85vh] max-w-[90vw] overflow-hidden rounded-lg border border-stone-700 bg-stone-900"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={gallery.images[activeIndex]}
              alt={`${gallery.title} - Stor visning ${activeIndex + 1}`}
              className="max-h-[80vh] max-w-full object-contain mx-auto"
            />
            <div className="bg-[#071018] px-4 py-3 text-center text-xs text-stone-400 border-t border-stone-700">
              Billede {activeIndex + 1} af {gallery.images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}